import React from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@vehix/portal/components/ui/popover';
import { formatInZone, formatTimeUntil, getNextRuns, tzAbbrev } from '../../utils/cronNextRun';
import type { ScheduleRow } from '../../utils/scheduleRows';

/**
 * Shown when the configured wall-clock time falls in the spring-forward gap.
 * Without this the row would display a time that does not exist that day.
 */
const DST_SHIFT_NOTE =
  "This wall-clock time doesn't exist on this date because of the daylight-saving " +
  'change. The run fires at the first valid minute after the gap.';

/** Occurrences listed in the popover — enough to confirm the cadence is right. */
const UPCOMING_COUNT = 5;

/**
 * The next few occurrences, so a cadence can be verified at a glance.
 *
 * A single next-run time doesn't distinguish "every weekday" from "every day"
 * — seeing the run after it does. Dagster surfaces upcoming ticks the same way.
 */
const UpcomingRuns: React.FC<{ row: ScheduleRow; now: Date }> = ({ row, now }) => {
  const s = row.schedule;
  const runs = getNextRuns(s.cron_expression, s.time_zone, { from: now, count: UPCOMING_COUNT });

  if (!runs || runs.length === 0) {
    return <p className='text-xs text-muted-foreground'>No upcoming runs to show.</p>;
  }

  return (
    <div className='space-y-1.5'>
      <p className='text-[10px] font-semibold uppercase tracking-wide text-muted-foreground'>
        Upcoming runs
      </p>
      <ul className='space-y-1'>
        {runs.map((run, i) => (
          <li key={run.instant.toISOString()} className='flex justify-between gap-2 text-xs'>
            <span className={i === 0 ? 'font-medium' : 'text-muted-foreground'}>
              {formatInZone(run.instant, s.time_zone, 'ddd, MMM D · h:mm A')}
            </span>
            <span className='shrink-0 text-muted-foreground'>
              {tzAbbrev(run.instant, s.time_zone)}
            </span>
          </li>
        ))}
      </ul>
      {!s.is_enabled && (
        <p className='text-[10px] italic text-muted-foreground'>
          Paused — would run at these times.
        </p>
      )}
    </div>
  );
};

interface ScheduleNextRunCellProps {
  row: ScheduleRow;
  now: Date;
  /** The reader's own zone, so a cross-zone schedule can show both readings. */
  viewerZone: string;
}

/**
 * When a schedule fires next, in its own timezone first.
 *
 * The schedule's zone leads because that's what the edit dialog stores and
 * what an operator reasons in; the viewer's local reading follows only when
 * the two differ, so a same-zone schedule isn't cluttered with a duplicate.
 */
// eslint-disable-next-line max-lines-per-function
export const ScheduleNextRunCell: React.FC<ScheduleNextRunCellProps> = ({
  row,
  now,
  viewerZone
}) => {
  const { schedule, next } = row;

  // Unreadable cron or unknown zone. Never guess — the cadence column shows
  // the raw expression alongside this.
  if (!next) {
    return (
      <div
        className='flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-500'
        title={`Can't predict runs for "${schedule.cron_expression}" (${schedule.time_zone})`}
      >
        <AlertTriangle className='h-3.5 w-3.5 shrink-0' />
        <span>Can&apos;t predict</span>
      </div>
    );
  }

  const zoneTime = formatInZone(next.instant, schedule.time_zone, 'ddd, MMM D · h:mm A');
  const zoneAbbrev = tzAbbrev(next.instant, schedule.time_zone);
  const showLocal = viewerZone !== schedule.time_zone;
  const localTime = formatInZone(next.instant, viewerZone, 'h:mm A');
  const localAbbrev = tzAbbrev(next.instant, viewerZone);

  // A paused schedule still shows when it *would* run: someone about to resume
  // it needs to know what they're switching on.
  if (!schedule.is_enabled) {
    return (
      <div className='text-xs'>
        <p className='italic text-muted-foreground'>Paused</p>
        <p
          className='text-muted-foreground opacity-60'
          title={`Would run ${zoneTime} ${zoneAbbrev}`}
        >
          would run {zoneTime} {zoneAbbrev}
        </p>
      </div>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type='button' className='text-left text-xs hover:underline'>
          <p className='flex items-center gap-1 font-medium'>
            <span>
              {zoneTime} {zoneAbbrev}
            </span>
            {/* lucide icons take no `title`, so the tooltip lives on a wrapper. */}
            {next.dstShifted && (
              <span title={DST_SHIFT_NOTE} aria-label='Shifted by daylight saving'>
                <Clock className='h-3 w-3 shrink-0 text-amber-500' />
              </span>
            )}
          </p>
          <p className='text-muted-foreground'>
            {formatTimeUntil(next.instant, now)}
            {showLocal && (
              <>
                {' · '}
                {localTime} {localAbbrev} your time
              </>
            )}
          </p>
        </button>
      </PopoverTrigger>
      <PopoverContent align='start' className='w-64 p-3'>
        <UpcomingRuns row={row} now={now} />
      </PopoverContent>
    </Popover>
  );
};
